/**
 * Semantic theme generator. A theme is a function of three inputs, as in
 * Linear's theme engine: a base (the neutral cast), an accent (the brand
 * pair) and a contrast level. `contrast` runs from 0 (standard) to 1 (high
 * contrast); every neutral, status and focus token interpolates its OKLCH
 * lightness between a standard and a high-contrast target, so the
 * high-contrast themes are generated, not hand-tuned.
 */
import { composite, contrastRatio, formatHex, oklchToHex, parseHex } from './color.js';
import { LOGO, type Mode } from './ramps.js';

export interface ThemeInputs {
  mode: Mode;
  /** 0 = standard, 1 = high contrast. */
  contrast: number;
  /** Neutral cast: hue and chroma multiplier (1 = the house navy cast). */
  base?: { hue: number; chromaScale: number };
}

/** [lightness at contrast 0, lightness at contrast 1, chroma at base scale 1] */
type NeutralSpec = readonly [number, number, number];

const NEUTRALS: Record<Mode, Record<string, NeutralSpec>> = {
  dark: {
    'surface-0': [0.177, 0.135, 0.013],
    'surface-1': [0.214, 0.165, 0.019],
    'surface-2': [0.248, 0.195, 0.022],
    'surface-3': [0.285, 0.225, 0.025],
    'surface-sunken': [0.192, 0.145, 0.016],
    'surface-hover': [0.27, 0.255, 0.024],
    'surface-active': [0.305, 0.3, 0.026],
    'border-subtle': [0.278, 0.34, 0.027],
    border: [0.333, 0.5, 0.03],
    'border-strong': [0.433, 0.66, 0.045],
    'border-control': [0.535, 0.7, 0.04],
    text: [0.94, 0.985, 0.01],
    'text-muted': [0.765, 0.9, 0.024],
    'text-dim': [0.66, 0.82, 0.03],
    'text-disabled': [0.5, 0.6, 0.025],
  },
  light: {
    'surface-0': [0.973, 0.955, 0.003],
    'surface-1': [1, 1, 0],
    'surface-2': [0.985, 0.975, 0.002],
    'surface-3': [1, 1, 0],
    'surface-sunken': [0.965, 0.95, 0.004],
    'surface-hover': [0.955, 0.93, 0.006],
    'surface-active': [0.93, 0.9, 0.008],
    'border-subtle': [0.925, 0.8, 0.007],
    border: [0.854, 0.6, 0.013],
    'border-strong': [0.672, 0.45, 0.024],
    'border-control': [0.63, 0.42, 0.024],
    text: [0.213, 0.1, 0.013],
    'text-muted': [0.413, 0.25, 0.025],
    'text-dim': [0.496, 0.32, 0.027],
    'text-disabled': [0.72, 0.6, 0.015],
  },
};

export const STATUS_TONES = ['danger', 'warning', 'success', 'info'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

/** Hue and chroma per tone; warning is ochre (hue 75-80), never brand orange. */
const STATUS_HUES: Record<StatusTone, { hue: number; chroma: number }> = {
  danger: { hue: 20, chroma: 0.19 },
  warning: { hue: 78, chroma: 0.13 },
  success: { hue: 160, chroma: 0.14 },
  info: { hue: 257, chroma: 0.15 },
};

/** Lightness of the text-on-surface status colour: [standard, high] per mode. */
const STATUS_TEXT_L: Record<Mode, Record<StatusTone, readonly [number, number]>> = {
  dark: {
    danger: [0.715, 0.8],
    warning: [0.807, 0.87],
    success: [0.763, 0.84],
    info: [0.729, 0.81],
  },
  light: {
    danger: [0.53, 0.42],
    warning: [0.5, 0.4],
    success: [0.514, 0.4],
    info: [0.496, 0.4],
  },
};

/**
 * Categorical chart and office colours. Order matters: office 1 gets the
 * brand orange, office 2 a strong blue, and so on, so the first few series are
 * maximally distinct.
 */
const CHART_HUES: readonly { hue: number; chroma: number }[] = [
  { hue: 58, chroma: 0.16 },
  { hue: 255, chroma: 0.15 },
  { hue: 170, chroma: 0.12 },
  { hue: 305, chroma: 0.15 },
  { hue: 90, chroma: 0.13 },
  { hue: 15, chroma: 0.16 },
  { hue: 210, chroma: 0.11 },
  { hue: 135, chroma: 0.13 },
];
const CHART_L: Record<Mode, readonly [number, number]> = { dark: [0.74, 0.8], light: [0.6, 0.5] };

export const CHART_COUNT = CHART_HUES.length;

/**
 * The tonal accent (owner, 2026-10-16: "they would look better with
 * transparency"): the logo orange as a translucent fill with a 1px border
 * and a readable orange label, in place of solid orange on primary actions,
 * icon tiles and checked controls. The alphas composite over whatever
 * surface the control sits on; contrast.ts checks the label on every
 * surface, at rest, hovered and pressed.
 */
export const TONAL_ALPHA: Record<
  Mode,
  { bg: number; hover: number; active: number; border: number }
> = {
  dark: { bg: 0.18, hover: 0.23, active: 0.28, border: 0.4 },
  light: { bg: 0.14, hover: 0.19, active: 0.24, border: 0.45 },
};

/** Label and icon orange on the tonal fill: [standard, high contrast] lightness per mode. */
const TONAL_FG_L: Record<Mode, readonly [number, number]> = {
  dark: [0.82, 0.9],
  light: [0.47, 0.4],
};

/** The readable orange for text and icon strokes on the tonal fill. */
export function tonalAccentFg(mode: Mode, contrast = 0): string {
  const [l0, l1] = TONAL_FG_L[mode];
  return oklchToHex({ l: lerp(l0, l1, contrast), c: 0.13, h: mode === 'dark' ? 55 : 45 });
}

/**
 * The `accent-soft-*` tokens for one mode, with the label colour `fg`.
 * `accent-soft-opaque` is the pressed tint flattened onto `surface`, for
 * small marks that overlap an icon (count badges) and must not show it
 * through.
 */
export function tonalAccent(mode: Mode, fg: string, surface: string): ThemeTokens {
  const a = TONAL_ALPHA[mode];
  // The logo orange thinned over a surface drifts toward yellow (hue 70+),
  // which the house palette rules out, so the tint is a redder orange: hue 52
  // (the Velora orange family) on dark, the deep logo orange on light.
  const ink = mode === 'dark' ? oklchToHex({ l: 0.72, c: 0.17, h: 52 }) : LOGO.orangeDeep;
  const tint = (alpha: number) => formatHex(parseHex(ink), alpha);
  return {
    'accent-soft-bg': tint(a.bg),
    'accent-soft-bg-hover': tint(a.hover),
    'accent-soft-bg-active': tint(a.active),
    'accent-soft-border': tint(a.border),
    'accent-soft-fg': fg,
    'accent-soft-opaque': composite(ink, a.active, surface),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export type ThemeTokens = Record<string, string>;

/** Composes the semantic token map for one theme. Values are hex or CSS expressions. */
export function generateTheme(inputs: ThemeInputs): ThemeTokens {
  const { mode } = inputs;
  const k = Math.min(1, Math.max(0, inputs.contrast));
  const baseHue = inputs.base?.hue ?? 264;
  const chromaScale = inputs.base?.chromaScale ?? 1;
  const t: ThemeTokens = {};

  for (const [name, [l0, l1, c]] of Object.entries(NEUTRALS[mode])) {
    t[name] = oklchToHex({ l: lerp(l0, l1, k), c: c * chromaScale, h: baseHue });
  }

  // Brand and action. In dark mode orange carries action and navy is the
  // surface cast; in light mode navy carries action and orange is the spark.
  if (mode === 'dark') {
    t['primary'] = LOGO.orange;
    t['primary-hover'] = LOGO.orangeLight;
    t['primary-active'] = LOGO.orangeDeep;
    t['primary-fg'] = LOGO.ink;
    t['accent'] = LOGO.orange;
    t['accent-strong'] = LOGO.orangeDeep;
    t['accent-text'] = k > 0.5 ? oklchToHex({ l: 0.86, c: 0.13, h: 75 }) : LOGO.orangeLight;
    t['accent-soft'] = oklchToHex({ l: lerp(0.27, 0.22, k), c: 0.045, h: 60 });
    t['focus-ring'] = k > 0.5 ? oklchToHex({ l: 0.88, c: 0.14, h: 80 }) : LOGO.orangeLight;
    t['selection'] = oklchToHex({ l: lerp(0.3, 0.28, k), c: 0.06, h: 262 });
    t['row-selected'] = oklchToHex({ l: lerp(0.255, 0.22, k), c: 0.04, h: 262 });
    t['navy-cast'] = oklchToHex({ l: 0.24, c: 0.05, h: 262 });
  } else {
    t['primary'] = k > 0.5 ? oklchToHex({ l: 0.16, c: 0.06, h: 262 }) : LOGO.ink;
    t['primary-hover'] = '#12295a';
    t['primary-active'] = LOGO.navy;
    t['primary-fg'] = '#ffffff';
    t['accent'] = LOGO.orange;
    t['accent-strong'] = LOGO.orangeDeep;
    t['accent-text'] = k > 0.5 ? oklchToHex({ l: 0.44, c: 0.13, h: 43 }) : LOGO.orangeText;
    t['accent-soft'] = oklchToHex({ l: lerp(0.965, 0.95, k), c: 0.03, h: 70 });
    t['focus-ring'] = k > 0.5 ? oklchToHex({ l: 0.3, c: 0.12, h: 260 }) : LOGO.navy;
    t['selection'] = oklchToHex({ l: lerp(0.9, 0.86, k), c: 0.04, h: 262 });
    t['row-selected'] = oklchToHex({ l: lerp(0.958, 0.93, k), c: 0.018, h: 262 });
    t['navy-cast'] = oklchToHex({ l: 0.963, c: 0.012, h: 260 });
  }
  Object.assign(t, tonalAccent(mode, tonalAccentFg(mode, k), t['surface-1'] as string));
  t['brand-gradient'] =
    `linear-gradient(135deg, ${LOGO.orangeLight} 0%, ${LOGO.orange} 45%, ${LOGO.orangeDeep} 100%)`;
  t['brand-gradient-vertical'] =
    `linear-gradient(180deg, ${LOGO.orangeLight} 0%, ${LOGO.orange} 45%, ${LOGO.orangeDeep} 100%)`;
  t['brand-gradient-fg'] = LOGO.ink;
  t['brand-ink'] = mode === 'dark' ? '#ffffff' : LOGO.ink;
  // The mark's tooth ribbon and the wordmark: logo navy on light; on dark the
  // navy is lifted to a cool periwinkle so the ribbon reads on charcoal.
  t['brand-mark-ink'] = mode === 'dark' ? oklchToHex({ l: 0.93, c: 0.022, h: 262 }) : LOGO.ink;
  t['brand-mark-ink-2'] = mode === 'dark' ? oklchToHex({ l: 0.66, c: 0.105, h: 262 }) : LOGO.navy;
  // SVG luminance masks (the mark's ribbon knock-out): opaque and clear.
  t['mask-keep'] = '#ffffff';
  t['mask-cut'] = '#000000';
  t['brand-orange-light'] = LOGO.orangeLight;
  t['brand-orange'] = LOGO.orange;
  t['brand-orange-deep'] = LOGO.orangeDeep;

  for (const tone of STATUS_TONES) {
    const { hue, chroma } = STATUS_HUES[tone];
    const [s0, s1] = STATUS_TEXT_L[mode][tone];
    t[tone] = oklchToHex({ l: lerp(s0, s1, k), c: chroma, h: hue });
    if (mode === 'dark') {
      t[`${tone}-soft`] = oklchToHex({ l: lerp(0.265, 0.22, k), c: chroma * 0.3, h: hue });
      t[`${tone}-border`] = oklchToHex({ l: lerp(0.42, 0.6, k), c: chroma * 0.55, h: hue });
      t[`${tone}-solid`] = oklchToHex({ l: lerp(0.52, 0.47, k), c: chroma, h: hue });
    } else {
      t[`${tone}-soft`] = oklchToHex({ l: lerp(0.965, 0.975, k), c: chroma * 0.17, h: hue });
      t[`${tone}-border`] = oklchToHex({ l: lerp(0.84, 0.6, k), c: chroma * 0.5, h: hue });
      t[`${tone}-solid`] = oklchToHex({ l: lerp(0.5, 0.42, k), c: chroma, h: hue });
    }
    t[`${tone}-solid-fg`] = '#ffffff';
  }

  // Signal yellow: the schedule's "Arrived" corner light (owner request, the
  // Eaglesoft convention). Status only; the house no-gold rule exempts it.
  // Always paired with a dark glyph, so it reads in both modes.
  t['signal-yellow'] = oklchToHex({ l: mode === 'dark' ? 0.89 : 0.87, c: 0.175, h: 98 });
  t['signal-yellow-fg'] = oklchToHex({ l: 0.25, c: 0.05, h: 98 });
  // "Here" (arrived) flow status: a light tint of the brand orange, not
  // yellow (front-desk role test FD-1/FD-21: no yellow or gold). Lighter and
  // less saturated than "Left message" orange so the two read apart.
  t['signal-here'] = oklchToHex({ l: 0.83, c: 0.11, h: 58 });
  t['signal-here-fg'] = oklchToHex({ l: 0.25, c: 0.05, h: 58 });

  CHART_HUES.forEach(({ hue, chroma }, i) => {
    const [c0, c1] = CHART_L[mode];
    t[`chart-${i + 1}`] = oklchToHex({ l: lerp(c0, c1, k), c: chroma, h: hue });
  });
  t['chart-grid'] = t['border-subtle'] as string;
  t['chart-axis'] = t['text-dim'] as string;
  // De-emphasis fill: context marks when one mark is the point, and the
  // remainder of a ring meter. Never a series colour.
  t['chart-muted'] = oklchToHex({
    l: mode === 'dark' ? lerp(0.45, 0.55, k) : lerp(0.8, 0.7, k),
    c: 0.012 * chromaScale,
    h: baseHue,
  });

  if (mode === 'dark') {
    t['chrome-glass'] = oklchToHex({ l: 0.214, c: 0.019, h: baseHue }, k > 0.5 ? 0.96 : 0.72);
    t['overlay'] = oklchToHex({ l: 0.1, c: 0.01, h: baseHue }, 0.7);
    t['highlight-inset'] = `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.04)}`;
    t['shadow-1'] =
      `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.04)}, 0 1px 2px ${oklchToHex({ l: 0, c: 0, h: 0 }, 0.3)}`;
    t['shadow-2'] =
      `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.05)}, 0 4px 12px ${oklchToHex({ l: 0, c: 0, h: 0 }, 0.35)}`;
    t['shadow-popover'] =
      `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.06)}, 0 12px 32px ${oklchToHex({ l: 0, c: 0, h: 0 }, 0.5)}`;
    t['shadow-hover'] =
      `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.06)}, 0 10px 28px -8px ${oklchToHex({ l: 0, c: 0, h: 0 }, 0.55)}`;
    t['card-sheen'] =
      `linear-gradient(180deg, ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.022)} 0%, ${oklchToHex({ l: 1, c: 0, h: 0 }, 0)} 60%)`;
    t['glow-brand'] =
      `0 0 0 1px ${oklchToHex({ l: 0.76, c: 0.16, h: 62 }, 0.35)}, 0 8px 28px -6px ${oklchToHex({ l: 0.7, c: 0.18, h: 55 }, 0.45)}`;
    t['skeleton'] = oklchToHex({ l: 0.27, c: 0.02, h: baseHue });
    t['skeleton-shine'] = oklchToHex({ l: 0.32, c: 0.022, h: baseHue });
  } else {
    const shadowInk = (a: number) => oklchToHex({ l: 0.2, c: 0.03, h: baseHue }, a);
    t['chrome-glass'] = oklchToHex({ l: 1, c: 0, h: 0 }, k > 0.5 ? 0.97 : 0.78);
    t['overlay'] = oklchToHex({ l: 0.225, c: 0.066, h: 262 }, 0.35);
    t['highlight-inset'] = `inset 0 1px 0 ${oklchToHex({ l: 1, c: 0, h: 0 }, 0.6)}`;
    t['shadow-1'] = `0 1px 2px ${shadowInk(0.06)}`;
    t['shadow-2'] = `0 2px 8px ${shadowInk(0.08)}, 0 1px 2px ${shadowInk(0.06)}`;
    t['shadow-popover'] = `0 8px 24px ${shadowInk(0.14)}, 0 1px 3px ${shadowInk(0.08)}`;
    t['shadow-hover'] = `0 1px 2px ${shadowInk(0.06)}, 0 10px 24px -8px ${shadowInk(0.18)}`;
    t['card-sheen'] = 'none';
    t['glow-brand'] =
      `0 0 0 1px ${oklchToHex({ l: 0.7, c: 0.17, h: 55 }, 0.3)}, 0 8px 24px -8px ${oklchToHex({ l: 0.65, c: 0.18, h: 50 }, 0.4)}`;
    t['skeleton'] = oklchToHex({ l: 0.93, c: 0.006, h: baseHue });
    t['skeleton-shine'] = oklchToHex({ l: 0.965, c: 0.004, h: baseHue });
  }
  return t;
}

export const CONTRAST_LEVELS = { standard: 0, high: 1 } as const;
export type ContrastLevel = keyof typeof CONTRAST_LEVELS;

export interface ThemeVariant {
  id: string;
  mode: Mode;
  contrast: ContrastLevel;
  tokens: ThemeTokens;
}

export function generateThemeVariants(): ThemeVariant[] {
  const variants: ThemeVariant[] = [];
  for (const mode of ['dark', 'light'] as const) {
    for (const contrast of ['standard', 'high'] as const) {
      variants.push({
        id: contrast === 'standard' ? mode : `${mode}-hc`,
        mode,
        contrast,
        tokens: generateTheme({ mode, contrast: CONTRAST_LEVELS[contrast] }),
      });
    }
  }
  return variants;
}

/** Re-exported for consumers who want to test their own pairs. */
export { contrastRatio };
